// The kinds of value a DAG skill input can hold, and the fields of each.
// No database or Node imports, so packages/app can import it.

/** The kinds of value an input, output or field can hold. */
export const DAG_VALUE_TYPES = [
  "task",
  "project",
  "status",
  "user",
  "canvas",
  "file",
  "team",
  "text",
  "date",
  "list",
] as const;
export type DagValueType = (typeof DAG_VALUE_TYPES)[number];

/** What a list holds: a value type, or another list with its own item type. */
export type DagItemType = DagValueType | { type: "list"; of: DagItemType };

/** A value's type as written on an input or field: a list may say what it holds. */
export function dagTypeOf(
  type: DagValueType,
  of: DagItemType | undefined,
): DagItemType {
  return type === "list" && of !== undefined ? { type: "list", of } : type;
}

export function dagTypeName(type: DagItemType): DagValueType {
  return typeof type === "string" ? type : type.type;
}

/** What each item of a list is, when the list says. */
export function dagItemOf(type: DagItemType): DagItemType | undefined {
  return typeof type === "string" ? undefined : type.of;
}

/** What each type is called, one and many, so a list names its items in the plural. */
export type DagTypeNames = Readonly<
  Record<DagValueType, { one: string; many: string }>
>;

// A record, so a type added above fails to compile until it has names.
const DAG_TYPE_NAMES: DagTypeNames = {
  task: { one: "task", many: "tasks" },
  project: { one: "project", many: "projects" },
  status: { one: "status", many: "statuses" },
  user: { one: "user", many: "users" },
  canvas: { one: "canvas", many: "canvases" },
  file: { one: "file", many: "files" },
  team: { one: "team", many: "teams" },
  text: { one: "text", many: "texts" },
  date: { one: "date", many: "dates" },
  list: { one: "list", many: "lists" },
};

/** A type read with `names`: `task`, `list of tasks`, `list of lists of files`. */
export function describeDagTypeWith(
  type: DagItemType,
  names: DagTypeNames,
): string {
  return typeof type === "string"
    ? names[type].one
    : `${names.list.one} of ${describeDagTypes(type.of, names)}`;
}

// The plural of a list's item type, which may itself be a list.
function describeDagTypes(type: DagItemType, names: DagTypeNames): string {
  return typeof type === "string"
    ? names[type].many
    : `${names.list.many} of ${describeDagTypes(type.of, names)}`;
}

/** A type as the agent reads it: `task`, `list of tasks`, `list of lists of files`. */
export function describeDagType(type: DagItemType): string {
  return describeDagTypeWith(type, DAG_TYPE_NAMES);
}

export type DagField = {
  // The field's name under its value, such as `title` in `{{task.title}}`.
  path: string;
  label: string;
  type: DagValueType;
  // For a list: what each item is.
  of?: DagItemType;
};

/** The fields a value of each object type exposes to `@` references. */
export const DAG_TYPE_FIELDS: Readonly<
  Record<DagValueType, readonly DagField[]>
> = {
  task: [
    { path: "id", label: "ID", type: "text" },
    { path: "title", label: "Title", type: "text" },
    { path: "description", label: "Description", type: "text" },
    { path: "due_at", label: "Due date", type: "date" },
    { path: "status", label: "Status", type: "status" },
    { path: "attachments", label: "Attachments", type: "list", of: "file" },
  ],
  project: [
    { path: "id", label: "ID", type: "text" },
    { path: "title", label: "Title", type: "text" },
    { path: "status", label: "Status", type: "text" },
    { path: "tasks", label: "Tasks", type: "list", of: "task" },
  ],
  // A status reads as its name.
  status: [],
  user: [
    { path: "id", label: "ID", type: "text" },
    { path: "name", label: "Name", type: "text" },
    { path: "email", label: "Email", type: "text" },
  ],
  // People pick these by name, so no id is offered.
  canvas: [{ path: "name", label: "Name", type: "text" }],
  file: [{ path: "name", label: "Name", type: "text" }],
  team: [{ path: "name", label: "Name", type: "text" }],
  text: [],
  date: [],
  list: [],
};
